import type { Region, Species, SpeciesRegion, SpeciesRegionalField } from '@api/types'
import { useRegions } from '@features/places/usePlaces'
import { useI18n } from '@i18n/I18nProvider'
import type { MessageKey } from '@i18n/messages'
import { PhotoCarousel } from '@ui/PhotoCarousel'
import { useState } from 'react'

const SECTIONS: [SpeciesRegionalField, MessageKey][] = [
	['characteristics', 'species.characteristics'],
	['plantingZone', 'species.plantingZone'],
	['symbology', 'species.symbology'],
	['culturalReferences', 'species.culturalReferences'],
]

// La pestaña con la que se abre: la pedida (`?comunidad=` o la del árbol) si la especie la tiene; si no, la primera.
export const initialRegion = (regions: readonly SpeciesRegion[], wanted: string | null): string | null =>
	regions.find((r) => r.regionId === wanted)?.regionId ?? regions[0]?.regionId ?? null

// Los apartados con texto de una comunidad, en su orden; un campo sin texto no sale.
export const regionSections = (region: SpeciesRegion): [MessageKey, string][] =>
	SECTIONS.flatMap(([field, title]) => {
		const text = region[field]?.trim()
		return text ? [[title, text] as [MessageKey, string]] : []
	})

// Nombre de la comunidad; mientras no carga el listado (o si no está), su código.
export const regionName = (regions: readonly Region[] | undefined, id: string): string =>
	regions?.find((r) => r.id === id)?.name ?? id

// El contenido de la ficha de una especie (spec `species`): fotos en carrusel, nombres y una
// pestaña por comunidad con sus textos. Lo usan la pantalla de la especie y la hoja que se abre
// desde un árbol (allí el nombre común va en el título de la hoja: `showName` false).
export const SpeciesDetails = ({
	species,
	wantedRegion,
	showName = true,
}: {
	species: Species
	wantedRegion: string | null
	showName?: boolean
}) => {
	const { t } = useI18n()
	const regions = useRegions()
	const [chosen, setChosen] = useState<string | null>(null)
	const selectedId = initialRegion(species.regions, chosen ?? wantedRegion)
	const selected = species.regions.find((r) => r.regionId === selectedId)
	const sections = selected ? regionSections(selected) : []
	return (
		<>
			<PhotoCarousel photos={species.photoUrls} alt={species.commonName} />
			{showName && <h1 className="text-3xl leading-tight">{species.commonName}</h1>}
			<p className="mb-4 text-lg text-earth-800 italic">{species.scientificName}</p>

			{species.regions.length > 0 && (
				<div role="tablist" aria-label={t('species.regions')} className="-mx-1 flex flex-wrap gap-2">
					{species.regions.map((region) => {
						const active = region.regionId === selectedId
						return (
							<button
								key={region.regionId}
								type="button"
								role="tab"
								id={`tab-${region.regionId}`}
								aria-selected={active}
								aria-controls="species-region-panel"
								onClick={() => setChosen(region.regionId)}
								className={`min-h-12 rounded-full border-2 px-4 font-semibold ${
									active
										? 'border-forest-800 bg-forest-800 text-white'
										: 'border-earth-300 bg-white text-forest-800 active:bg-earth-100'
								}`}
							>
								{regionName(regions.data?.items, region.regionId)}
							</button>
						)
					})}
				</div>
			)}

			{selected && (
				<div role="tabpanel" id="species-region-panel" aria-labelledby={`tab-${selected.regionId}`}>
					{sections.length === 0 && (
						<p className="mt-5 text-lg text-earth-800">{t('species.noRegionTexts')}</p>
					)}
					{sections.map(([title, text]) => (
						<section key={title} className="mt-5">
							<h2 className="mb-1 text-xl font-semibold">{t(title)}</h2>
							<p className="text-lg">{text}</p>
						</section>
					))}
				</div>
			)}
		</>
	)
}
